import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { X, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { dismissWalkthrough } from '@/lib/user-settings';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';

interface WalkthroughProps {
    userId: string;
    onDismiss: () => void;
}

interface Step {
    targetId: string;
    title: string;
    content: string;
    position: 'bottom' | 'top' | 'left' | 'right';
}

const STEPS: Step[] = [
    {
        targetId: 'tour-passage-selector',
        title: 'Choose Your Passage',
        content: 'You can scroll through the text or click here to quickly jump to any book or chapter in the Bible.',
        position: 'bottom',
    },
    {
        targetId: 'tour-dig-mode',
        title: 'Deepen Your Study',
        content: 'When you are ready to study a passage, click this to enter Dig Mode. It provides Ancient, Biblical, and Modern context tools.',
        position: 'bottom',
    }
];

export function Walkthrough({ userId, onDismiss }: WalkthroughProps) {
    const [currentStep, setCurrentStep] = useState(0);
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, height: 0 });
    const [isVisible, setIsVisible] = useState(false);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const isDark = useDarkMode();
    const step = STEPS[currentStep];

    useEffect(() => {
        const updateCoords = () => {
            const target = document.getElementById(step.targetId);
            if (target) {
                const rect = target.getBoundingClientRect();
                setCoords({
                    top: rect.top + window.scrollY,
                    left: rect.left + window.scrollX,
                    width: rect.width,
                    height: rect.height,
                });
                setIsVisible(true);
            } else {
                setIsVisible(false);
            }
        };

        // Initial update
        updateCoords();

        // Update on resize/scroll
        window.addEventListener('resize', updateCoords);
        window.addEventListener('scroll', updateCoords);

        // Polling as a fallback (some components might mount/render late)
        const interval = setInterval(updateCoords, 500);

        return () => {
            window.removeEventListener('resize', updateCoords);
            window.removeEventListener('scroll', updateCoords);
            clearInterval(interval);
        };
    }, [currentStep, step.targetId]);

    const handleNext = () => {
        if (currentStep < STEPS.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            handleComplete();
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const handleComplete = async () => {
        await dismissWalkthrough(userId, true);
        onDismiss();
    };

    // Debug: Add a keyboard shortcut to reset the walkthrough for testing
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'R') {
                e.preventDefault();
                dismissWalkthrough(userId, false).then(() => {
                    window.location.reload();
                });
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [userId]);

    if (!isVisible) return null;

    const tooltipLeft = Math.max(16, Math.min(window.innerWidth - (tooltipRef.current?.offsetWidth || 336), (coords.left + coords.width / 2) - (tooltipRef.current?.offsetWidth || 320) / 2));
    const targetCenterX = coords.left + coords.width / 2;
    const arrowLeftOffset = targetCenterX - tooltipLeft;

    return (
        <div className="fixed inset-0 z-[100] pointer-events-none">
            {/* Removed Spotlight Overlay per user request */}

            {/* Tooltip */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentStep}
                    ref={tooltipRef}
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute pointer-events-auto w-[280px] sm:w-[320px]"
                    style={{
                        top: coords.top + coords.height + 12,
                        left: tooltipLeft,
                    }}
                >
                    <div className="relative p-5 rounded-2xl bg-background/95 backdrop-blur-2xl border border-white/40 shadow-2xl overflow-hidden group">
                        {/* Animated gradient background */}
                        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/10 via-transparent to-primary/5 opacity-50" />

                        <button
                            onClick={handleComplete}
                            className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/10 transition-colors text-muted-foreground hover:text-foreground"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        <div className="space-y-4">
                            <div className="space-y-1">
                                <p className="text-xs font-bold tracking-widest uppercase text-primary/80">
                                    Step {currentStep + 1} of {STEPS.length}
                                </p>
                                <h3 className="text-lg font-bold tracking-tight">
                                    {step.title}
                                </h3>
                            </div>

                            <p className="text-sm text-foreground/90 leading-relaxed font-medium">
                                {step.content}
                            </p>

                            <div className="pt-2 flex items-center justify-between gap-3">
                                <div className="flex gap-1">
                                    {STEPS.map((_, i) => (
                                        <div
                                            key={i}
                                            className={`h-1 rounded-full transition-all duration-300 ${i === currentStep ? 'w-4 bg-primary' : 'w-1 bg-primary/20'}`}
                                        />
                                    ))}
                                </div>

                                <div className="flex items-center gap-2">
                                    {currentStep > 0 && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleBack}
                                            className="h-8 px-2"
                                        >
                                            <ChevronLeft className="w-4 h-4 mr-1" />
                                            Back
                                        </Button>
                                    )}
                                    <Button
                                        size="sm"
                                        onClick={handleNext}
                                        className="h-8 shadow-md shadow-primary/20"
                                    >
                                        {currentStep === STEPS.length - 1 ? (
                                            <>
                                                Got it
                                                <Check className="w-4 h-4 ml-1.5" />
                                            </>
                                        ) : (
                                            <>
                                                Next
                                                <ChevronRight className="w-4 h-4 ml-1.5" />
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tip indicator (the arrow) */}
                    <div
                        className="absolute -top-[10px] w-5 h-5 z-20 pointer-events-none"
                        style={{
                            left: arrowLeftOffset,
                            transform: 'translateX(-50%)',
                        }}
                    >
                        {/* Border layer - using a darker gray for visibility as requested */}
                        <div
                            className={`absolute inset-0 rotate-45 transition-colors duration-300 ${isDark ? 'bg-white/50' : 'bg-foreground/25'}`}
                            style={{ clipPath: 'polygon(0% 0%, 100% 0%, 0% 100%)' }}
                        />
                        {/* Background layer - slightly smaller to show border, matches tooltip background */}
                        <div
                            className="absolute inset-[1px] bg-background/80 backdrop-blur-2xl rotate-45 transition-colors duration-300"
                            style={{ clipPath: 'polygon(0% 0%, 100% 0%, 0% 100%)' }}
                        />
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
}
